import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import { getDepartmentUsers } from "../../../../../../services/departments";

const DepartmentUsersContext = createContext();

const initialState = {
  users: [],
  meta: null,
  loading: false,
  error: null,
  isEmpty: false,
};

export const DepartmentUsersProvider = ({ children }) => {
  const [state, setState] = useState(initialState);
  const fetchRequestIdRef = useRef(0);

  const fetchUsers = useCallback(
    async (params = {}) => {
      const department_uuid = params.department_uuid || params.departmentUuid;
      
      if (!department_uuid) {
        setState((prev) => ({
          ...prev,
          loading: false,
          error: new Error("department_uuid is required"),
          users: [],
          meta: null,
          isEmpty: false,
        }));
        return;
      }

      const requestId = ++fetchRequestIdRef.current;

      setState((prev) => ({
        ...prev,
        loading: true,
        error: null,
        isEmpty: false,
      }));

      try {
        const response = await getDepartmentUsers({
          department_uuid,
          page: params.page,
          page_size: params.page_size,
          order_by: params.order_by,
          order_direction: params.order_direction,
          search: params.search,
          filters: params.filters,
        });

        if (requestId !== fetchRequestIdRef.current) {
          setState((prev) => ({ ...prev, loading: false }));
          return;
        }

        const responseStatusCode = response?.statusCode || response?.status_code;
        const isValidResponse = responseStatusCode === 200;

        if (!isValidResponse) {
          const errorMessage = response?.msgCode?.[0]?.description || 
                               response?.msg_code?.[0]?.description || 
                               'Invalid response status code';
          throw new Error(errorMessage);
        }

        const body = response?.body || [];
        
        setState((prev) => ({
          ...prev,
          users: Array.isArray(body) && body.length > 0 ? body : [],
          meta: response.meta || null,
          loading: false,
          error: null,
          isEmpty: !Array.isArray(body) || body.length === 0,
        }));
      } catch (error) {
        if (requestId !== fetchRequestIdRef.current) {
          setState((prev) => ({ ...prev, loading: false }));
          return;
        }

        console.error("Error fetching department users:", error);
        setState((prev) => ({
          ...prev,
          loading: false,
          error,
          users: [],
          meta: null,
          isEmpty: false,
        }));
      }
    },
    []
  );

  const resetUsers = useCallback(() => {
    setState(initialState);
    fetchRequestIdRef.current = 0;
  }, []);

  const value = {
    users: state.users,
    meta: state.meta,
    loading: state.loading,
    error: state.error,
    isEmpty: state.isEmpty,
    fetchUsers,
    resetUsers,
  };

  return (
    <DepartmentUsersContext.Provider value={value}>
      {children}
    </DepartmentUsersContext.Provider>
  );
};

export const useDepartmentUsers = () => {
  const context = useContext(DepartmentUsersContext);
  if (!context) {
    throw new Error('useDepartmentUsers must be used within DepartmentUsersProvider');
  }
  return context;
};

